<template>
  <div class="ask-macys-prompts">
    <h6 class="grid-x" :class="{ 'margin-bottom-xxs': headingSmallMargin, 'margin-bottom-s': !headingSmallMargin }">
      <span>{{ heading }}</span>
      <img :src="starIconSrc" alt="" class="ml-4" />
    </h6>

    <div v-if="isMcom && isArmTwo && isSearching" class="margin-bottom-s">Want a little more guidance?</div>

    <template v-if="!isMcom && isSearching && hasSuggestions">
      <div class="ask-promts-copy margin-bottom-xs">Want some more guidance?</div>
      <button type="button" class="static-btn" @click="handleStaticClick">
        <img :src="staticPromtIconSrc" alt="" />
        <span>{{ staticBtnText }}</span>
      </button>
    </template>

    <div v-else-if="isMcom && (isLoading || (isSearching && !processed))" class="trending-queries-loading">
      <div class="nav-scroller">
        <template v-if="isArmTwo">
          <div class="nav-scroller-content gap-16">
            <button type="button" class="static-btn" @click="handleStaticClick">
              <img :src="staticPromtIconSrc" alt="" />
              <span>{{ staticBtnText }}</span>
            </button>
            <Skeleton v-for="n in 3" :key="n" height="36px" width="135px" class="ask-macys-prompts-skeleton" />
          </div>
          <div class="nav-scroller-content gap-16">
            <Skeleton v-for="n in 4" :key="n" height="36px" width="135px" class="ask-macys-prompts-skeleton" />
          </div>
        </template>
        <template v-else>
          <div class="nav-scroller-content gap-16">
            <Skeleton v-for="n in 3" :key="n" height="36px" width="120px" class="ask-macys-prompts-skeleton" />
            <button type="button" class="static-btn" @click="handleStaticClick">
              <img :src="staticPromtIconSrc" alt="" />
              <span>{{ staticBtnText }}</span>
            </button>
          </div>
        </template>
      </div>
    </div>

    <Scroller v-else ref="scrollerRef">
      <template v-if="isMcom && isArmTwo">
        <ul class="nav-scroller-content gap-16">
          <li class="static-pill-item">
            <button type="button" class="static-btn" @click="handleStaticClick">
              <img :src="staticPromtIconSrc" alt="" />
              <span>{{ staticBtnText }}</span>
            </button>
          </li>
          <li v-for="prompt in row1Prompts" :key="prompt" class="prompt-pill-item">
            <button type="button" class="dynamic-btn" @click="handlePromptClick(prompt)">
              <img :src="promtIconSrc" alt="" />
              <span>{{ prompt }}</span>
            </button>
          </li>
        </ul>
        <ul v-if="row2Prompts.length" class="nav-scroller-content gap-16">
          <li v-for="prompt in row2Prompts" :key="prompt" class="prompt-pill-item">
            <button type="button" class="dynamic-btn" @click="handlePromptClick(prompt)">
              <img :src="promtIconSrc" alt="" />
              <span>{{ prompt }}</span>
            </button>
          </li>
        </ul>
      </template>
      <template v-else>
        <ul class="nav-scroller-content gap-16">
          <li v-if="showStaticButtonFirst" class="static-pill-item">
            <button type="button" class="static-btn" @click="handleStaticClick">
              <img :src="staticPromtIconSrc" alt="" />
              <span>{{ staticBtnText }}</span>
            </button>
          </li>

          <li v-for="prompt in displayedPrompts" :key="prompt" class="prompt-pill-item">
            <button type="button" class="dynamic-btn" @click="handlePromptClick(prompt)">
              <img :src="promtIconSrc" alt="" />
              <span>{{ prompt }}</span>
            </button>
          </li>

          <li v-if="!showStaticButtonFirst" class="static-pill-item">
            <button type="button" class="static-btn" @click="handleStaticClick">
              <img :src="staticPromtIconSrc" alt="" />
              <span>{{ staticBtnText }}</span>
            </button>
          </li>
        </ul>
      </template>
    </Scroller>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useDebounceFn, useEventBus } from '@vueuse/core';
import { useNuxtApp } from '#app';
import { useNavHeader } from '~/stores/navHeader';
import { useEnvProps } from '~/stores/envProps';
import { usePageData } from '~/stores/pageData';
import { useNavUser } from '~/stores/navUser';
import { useNavViewType } from '~/stores/navViewType';
import { GET_IS_DESKTOP_VIEW } from '#shared/types/getters';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import { askMacysPrompts as configPrompts } from 'config';
import getVisitorId from '#shared/utils/visitorId';
import getAssetImage from '~/utils/getAssetImage.ts';
import devProxyApiUrl from '~/utils/devProxyApiUrl';
import { securityValidation } from '#shared/utils/StringUtil';
import Scroller from '~/components/common/Scroller.common';
import { tagStaticPromptClick, tagDynamicPromptClick } from '#shared/features/analytics/agentPromptsTags';
import { events, SEARCH } from '#shared/constants/eventBus';
import useExperimentsArms from '~/composables/useExperimentsArms';

const MIN_SEARCH_VALUE_LENGTH = 2;

defineProps({
  hasSuggestions: {
    type: Boolean,
    default: false,
  },
});

const { isMcom, hostlink } = useEnvProps();
const navHeader = useNavHeader();
const pageData = usePageData();
const navUser = useNavUser();
const navViewType = useNavViewType();
const bus = useEventBus(SEARCH);
const experimentsArms = useExperimentsArms();
const isArmTwo = computed(() => !!experimentsArms.value.askMacysPromptsArmTwo);

const heading = isMcom ? 'Ask Macy’s' : 'AI Chat';
const staticBtnText = isMcom ? 'Let’s chat' : 'Ask me anything';
const starIconSrc = computed(() => getAssetImage(isMcom ? 'askMacys.svg' : 'starIcon.svg'));
const promtIconSrc = getAssetImage('promtStar.svg');
const staticPromtIconSrc = computed(() => getAssetImage(isMcom ? 'promtStar.svg' : 'staticPromtStar.svg'));
const searchValue = computed(() => navHeader.searchValue);
const isSearching = computed(() => searchValue.value.trim().length >= MIN_SEARCH_VALUE_LENGTH);
const isInternational = computed(() => pageData.navigation.context.isInternational);
const headingSmallMargin = computed(() => (isMcom ? isArmTwo.value && isSearching.value : isSearching.value));

const scrollerRef = ref(null);
const isLoading = ref(false);
const processed = ref(false);
const apiPrompts = ref([]);

const displayedPrompts = computed(() => {
  if (isMcom && isSearching.value) return apiPrompts.value;
  return configPrompts;
});

const row1Prompts = computed(() => {
  return displayedPrompts.value.filter((_, i) => i % 2 === 0);
});

const row2Prompts = computed(() => {
  return displayedPrompts.value.filter((_, i) => i % 2 !== 0);
});

const showStaticButtonFirst = computed(() => !(isMcom && isSearching.value));

const handleStaticClick = () => {
  tagStaticPromptClick(isSearching.value);
  window.openAskAi({ source: 'SEARCH-PROMTS' });
  bus.emit(events[SEARCH].CLOSE);
};

const reportQacPromptsInView = (query, prompt_options) => {
  if (window.googleAgent && typeof window.googleAgent.reportShoppingMetric === 'function') {
    window.googleAgent.reportShoppingMetric('qac_prompts_in_view', {
      query,
      prompt_options,
    });
  }
};

const sendQueryToAgent = (promptText) => {
  if (window.googleAgent && typeof window.googleAgent.sendQuery === 'function') {
    window.googleAgent.sendQuery(promptText, { openReason: 'QAC_PROMPT_CLICK' });
    return true;
  }
  return false;
};

const handlePromptClick = (prompt) => {
  tagDynamicPromptClick(isSearching.value);

  const isAgentReadyAndPromtSent = sendQueryToAgent(prompt);
  if (!isAgentReadyAndPromtSent) {
    window.googleAgentOpen = true;
    window.pendingOpenAskAiPayload = { source: 'SEARCH-PROMTS' };
    window.dispatchEvent(new CustomEvent('google-agent-open'));
    const unhook = useNuxtApp().hook('ask-ai:google-agent-loaded', () => {
      if (window.googleAgentOpen) {
        sendQueryToAgent(prompt);
      }
      unhook();
    });
  }
  bus.emit(events[SEARCH].CLOSE);
};

const buildApiUrl = (query) => {
  const visitorId = getVisitorId();
  const userId = navUser.data.uid;
  const isDesktop = navViewType[GET_IS_DESKTOP_VIEW];
  const deviceType = isDesktop ? 'DESKTOP' : 'MOBILE';
  const store = isMcom ? 'MCOM' : 'BCOM';

  let url = `/searcher/v1/suggestions/public?prefix=${encodeURIComponent(query)}&store=${store}&_deviceType=${deviceType}`;
  if (userId) {
    url += `&userId=${encodeURIComponent(userId)}`;
  }
  if (visitorId) {
    url += `&visitorId=${encodeURIComponent(visitorId)}`;
  }
  return url;
};

const extractPrompts = (res) => {
  return res?.aiPrompts || [];
};

const fetchAgentPromts = useDebounceFn(async (input) => {
  if (!isMcom || !input) {
    apiPrompts.value = [];
    isLoading.value = false;
    processed.value = false;
    await nextTick();
    scrollerRef.value?.resetScroll();
    return;
  }

  const query = input.trim();

  if (query.length >= MIN_SEARCH_VALUE_LENGTH && securityValidation(query) && !isInternational.value) {
    const storageKey = `agent-prompts-for:${query.toLowerCase()}`;
    const cached = sessionStorage.getItem(storageKey);

    if (cached) {
      try {
        apiPrompts.value = JSON.parse(cached);
        processed.value = true;
        return;
      } catch {
        // Ignore cache parse error
      }
    }

    isLoading.value = true;
    try {
      const url = buildApiUrl(query);
      const proxy = devProxyApiUrl(hostlink);
      const res = await $fetch(proxy(url));
      const extractedPrompts = extractPrompts(res);

      apiPrompts.value = extractedPrompts;
      sessionStorage.setItem(storageKey, JSON.stringify(extractedPrompts));

      if (extractedPrompts.length) {
        reportQacPromptsInView(query, extractedPrompts);
      }
    } catch {
      apiPrompts.value = [];
    } finally {
      isLoading.value = false;
      processed.value = true;
      await nextTick();
      scrollerRef.value?.resetScroll();
    }
  } else {
    apiPrompts.value = [];
    isLoading.value = false;
    await nextTick();
    scrollerRef.value?.resetScroll();
  }
}, 300);

watch(searchValue, fetchAgentPromts, { immediate: true });
</script>

<style lang="scss" scoped>
@import '~/assets/scss/mixins/index.scss';

button {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 16px 0 8px;
  white-space: nowrap;
  cursor: pointer;

  &.dynamic-btn {
    background-color: $background-color;
    border: 1px solid $gray-2-color;
  }

  img {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }
}

.ml-4 {
  margin-left: 4px;
}
</style>

<style lang="scss" brand="mcom" scoped>
button {
  font-size: 14px;
  border-radius: 20px;
  &.static-btn {
    border: 1px solid transparent;
    background:
      linear-gradient(#fff, #fff) padding-box,
      linear-gradient(to right, #e22838, #77478e) border-box;
  }
}

.ask-macys-prompts-skeleton {
  border-radius: 20px;
}
</style>

<style lang="scss" brand="bcom" scoped>
h6 img {
  height: 18px;
}

button {
  font-size: 13px;
  &.static-btn {
    border: 1.5px solid transparent;
    background:
      linear-gradient(#fff, #fff) padding-box,
      linear-gradient(65deg, #f1cccc 8.45%, #df774e 91.55%) border-box;
  }
}

.ask-promts-copy {
  color: #626369;
}
</style>
